import React, { useEffect, useState, useContext } from "react";
import { useLocation, useHistory } from "react-router-dom";
import Button from "components/CustomButtons/Button.js";

// Context and APIs
import NotificationContext from "views/Components/Context.js";
import { validateInviteRequestAPI, responseToInviteAPI } from "views/APIs/APIUtility";

import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";

import {
    Typography,
    Paper,
    CircularProgress,
    makeStyles,
    Grid,
    Box,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
    ...styles,
    containerWrapper: {
        minHeight: "50vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: theme.spacing(2), // Optional: keeps padding on small screens
        boxSizing: "border-box",
    },
    paper: {
        padding: theme.spacing(4),
        width: "100%",
        maxWidth: 600,
        textAlign: "center",
    },
    title: {
        marginBottom: theme.spacing(2),
        fontWeight: 600,
    },
    detail: {
        marginTop: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    buttonsContainer: {
        marginTop: theme.spacing(4),
    },
}));

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const InvitationPage = () => {
    const classes = useStyles();
    const query = useQuery();
    const history = useHistory();
    const { showNotification } = useContext(NotificationContext);

    const [projectId, setProjectId] = useState("");
    const [email, setEmail] = useState("");
    const [projectType, setProjectType] = useState("");
    const [inviterName, setInviterName] = useState("");
    const [projectName, setProjectName] = useState("");

    const [isExpired, setIsExpired] = useState(false);
    const [isValidInvite, setIsValidInvite] = useState(false);
    const [responseMessage, setResponseMessage] = useState("");
    const [loading, setLoading] = useState({ loading: false, message: "Loading..." });
    const [buttonLoading, setButtonLoading] = useState(false);

    const writingTypes = ["white_paper", "blog", "article", "research_paper", "report", "key_insights", "case_study"];

    useEffect(() => {
        const fetchInviteDetails = async () => {
            const id = query.get("project_id");
            const userEmail = query.get("email");
            const type = query.get("collection_type");


            if (id && userEmail && type) {
                setProjectId(id);
                setEmail(userEmail);
                setProjectType(type);
                setLoading({ loading: true, message: "Validating invite..." });

                const normalizedType = writingTypes.includes(type)
                    ? "writing"
                    : type;

                const requestData = {
                    project_id: id,
                    collection_type: normalizedType,
                    invitee_email: userEmail,
                };

                try {

                    const response = await validateInviteRequestAPI(requestData);
                    if (response?.expired) setIsExpired(true);
                    if (response?.valid) setIsValidInvite(true);
                    if (response?.inviter_name) setInviterName(response.inviter_name);
                    if (response?.project_name) setProjectName(response.project_name);
                    setResponseMessage(response?.message);
                } catch {
                    showNotification("Something went wrong while validating the invite.", "danger");
                } finally {
                    setLoading({ loading: false, message: "" });
                }
            } else {
                showNotification("Missing or malformed invitation link.", "danger");
                setLoading({ loading: false, message: "" });
            }
        };

        fetchInviteDetails();
    }, []);

    const handleInviteAPI = async (requestData) => {
        const payload = {
            ...requestData,
            collection_type: writingTypes.includes(projectType) ? "writing" : projectType,
            project_id: projectId,
            email: email,
        };

        try {
            await responseToInviteAPI(payload);
        } catch {
            showNotification("Something went wrong while responding to the invite.", "danger");
        }
    };


    const handleRedirect = () => {
        let path = "/create/dashboard";
        switch (true) {
            case projectType === "course":
                path = `/create/course-design/courses/${projectId}`;
                break;
            case projectType === "lab":
                path = `/create/labdesign/${projectType}/${projectId}`;
                break;
            case writingTypes.includes(projectType):
                path = `/create/${projectType}/${projectId}`;
                break;
            case projectType === "podcast":
                path = `/create/podcastdesign/${projectType}/${projectId}`;
                break;
            default:
                console.warn("Unknown project type:", projectType);
                break;
        }
        history.push(path);
    };


    const handleAccept = async () => {
        setButtonLoading(true);
        await handleInviteAPI({ accepted: true });
        handleRedirect();
        setButtonLoading(false);
    };

    const handleDecline = async () => {
        setButtonLoading(true);
        await handleInviteAPI({ accepted: false });
        window.location.reload();
        setButtonLoading(false);
    };

    if (loading.loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress className={classes.loaderClass} />
                <Typography variant="h6" style={{ marginLeft: 16, textTransform: 'none' }}>
                    {loading.message}
                </Typography>
            </Box>
        );
    }

    return (
        <Box className={classes.containerWrapper}>
            <Paper className={classes.paper}>
                {!isExpired ? (
                    <>
                        <Typography variant="h5" className={classes.title}>
                            Collaboration Invitation
                        </Typography>
                        <Typography variant="body1">
                            {`${inviterName} has invited you to join a ${projectType.replace(/[\n\r\t\\]/g, '').replace(/_/g, ' ')} project.`}
                        </Typography>

                        <Typography variant="body2" className={classes.detail}>
                            {`Project: ${projectName}`}
                        </Typography>

                        <Box display="flex" justifyContent="center" alignItems="center" spacing={2}>
                            <Box m={1}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    color="quSandBoxColor"
                                    onClick={handleDecline}
                                    disabled={buttonLoading}
                                >
                                    {buttonLoading ? "Processing..." : "Decline"}
                                </Button>
                            </Box>

                            <Box m={1}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="quCreateColor"
                                    onClick={handleAccept}
                                    disabled={buttonLoading}
                                >
                                    {buttonLoading ? "Processing..." : "Accept"}
                                </Button>
                            </Box>
                        </Box>
                    </>
                ) : isValidInvite ? (
                    <>
                        <Typography variant="h5" className={classes.title}>
                            Invitation Already Accepted
                        </Typography>
                        <Typography variant="body1">
                            You've already accepted this invitation.{" "}
                            <span
                                style={{ color: "#3f51b5", cursor: "pointer", fontWeight: 500 }}
                                onClick={handleRedirect}
                            >
                                {projectName}
                            </span>
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="h5" className={classes.title}>
                            Invitation Unavailable
                        </Typography>
                        <Typography variant="body1">
                            {responseMessage ||
                                "This invitation is no longer valid or has been rejected."}
                        </Typography>
                    </>
                )}
            </Paper>
        </Box>
    );


};

export default InvitationPage;
